import React, { useMemo, useState } from "react";
import "./GanttView.scss";

const dayMs = 86400000;

const fmt = (d) =>
  d instanceof Date && !isNaN(d)
    ? d.toLocaleDateString(undefined, { month: "short", day: "numeric" })
    : "";

function toDateSafe(v) {
  const d = new Date(v);
  return isNaN(d) ? null : d;
}

function pctBetween(date, start, end) {
  if (!date) return 0;
  const t = date.getTime();
  return ((t - start.getTime()) / (end.getTime() - start.getTime())) * 100;
}

function computeProgress(task) {
  // Progress from checklist items across all todos (optional, safe defaults)
  const todos = Array.isArray(task?.todoRequestDTOList)
    ? task.todoRequestDTOList
    : [];
  let total = 0;
  let done = 0;
  for (const td of todos) {
    const list = Array.isArray(td?.toDoListResponseDTOList)
      ? td.toDoListResponseDTOList
      : [];
    for (const li of list) {
      total += 1;
      if (li?.completion) done += 1;
    }
  }
  if (!total) return null;
  return Math.max(0, Math.min(100, Math.round((done / total) * 100)));
}

function colorFor(task) {
  const v = String(task?.priority || task?.projectStatus || "").toUpperCase();
  if (v.includes("CRIT") || v.includes("BLOCK") || v.includes("DELAY"))
    return "bar--red";
  if (v.includes("HIGH") || v.includes("RISK")) return "bar--amber";
  if (v.includes("MED")) return "bar--blue";
  if (v.includes("LOW") || v.includes("TRACK")) return "bar--green";
  return "bar--gray";
}

export default function GanttView({ tasks = [] }) {
  // ---- timeline window
  const { start, end } = useMemo(() => {
    if (!tasks.length) {
      const today = new Date();
      return {
        start: new Date(today.getTime() - 7 * dayMs),
        end: new Date(today.getTime() + 21 * dayMs),
      };
    }
    const starts = tasks
      .map((t) => toDateSafe(t.startDate))
      .filter(Boolean)
      .map((d) => d.getTime());
    const ends = tasks
      .map((t) => toDateSafe(t.endDate))
      .filter(Boolean)
      .map((d) => d.getTime());

    if (!starts.length || !ends.length) {
      const today = new Date();
      return {
        start: new Date(today.getTime() - 7 * dayMs),
        end: new Date(today.getTime() + 21 * dayMs),
      };
    }
    // pad a little
    const s = new Date(Math.min(...starts) - 2 * dayMs);
    const e = new Date(Math.max(...ends) + 2 * dayMs);
    return { start: s, end: e };
  }, [tasks]);

  // ---- evenly spaced ticks (7)
  const ticks = useMemo(() => {
    const n = 6;
    const out = [];
    for (let i = 0; i <= n; i++) {
      out.push(
        new Date(start.getTime() + (i / n) * (end.getTime() - start.getTime())),
      );
    }
    return out;
  }, [start, end]);

  // tiny tooltip (no library)
  const [tip, setTip] = useState(null); // {x,y,lines:[]}

  return (
    <div className="gantt">
      <div className="gantt__head">
        <div className="gantt__range">
          {fmt(start)} → {fmt(end)}
        </div>
      </div>

      {/* header ticks + today line */}
      <div className="gantt__ticks">
        {ticks.map((t, i) => (
          <div
            key={i}
            className="gantt__tick"
            style={{ left: `${pctBetween(t, start, end)}%` }}
          >
            {fmt(t)}
          </div>
        ))}
        <div
          className="gantt__today"
          style={{ left: `${pctBetween(new Date(), start, end)}%` }}
          title="Today"
        />
      </div>

      {/* rows */}
      <div className="gantt__rows">
        {tasks.map((task, idx) => {
          const s = toDateSafe(task.startDate);
          const e = toDateSafe(task.endDate);
          const hasDates = s && e;

          const left = hasDates ? pctBetween(s, start, end) : 0;
          const width = hasDates
            ? Math.max(2, pctBetween(e, start, end) - left)
            : 2; // min width

          const progress = computeProgress(task); // % or null
          const colorClass = colorFor(task);
          const isActive =
            String(task?.priority || "")
              .toUpperCase()
              .includes("PROGRESS") ||
            String(task?.projectStatus || "")
              .toUpperCase()
              .includes("PROGRESS");

          const tipLines = [
            task.taskTitle || "Untitled task",
            hasDates ? `${fmt(s)} → ${fmt(e)}` : "Dates missing",
            task.taskOwnerName ? `Owner: ${task.taskOwnerName}` : null,
            typeof progress === "number" ? `Progress: ${progress}%` : null,
            task.priority ? `Priority: ${task.priority}` : null,
          ].filter(Boolean);

          return (
            <div key={task.taskId || idx} className="gantt__row">
              <div className="gantt__label" title={task.taskTitle}>
                <div className="gantt__labelTitle">{task.taskTitle}</div>
                {task.taskOwnerName && (
                  <div className="gantt__labelSub">{task.taskOwnerName}</div>
                )}
              </div>

              <div className="gantt__lane">
                {hasDates ? (
                  <button
                    className={[
                      "gantt__bar",
                      colorClass,
                      isActive ? "bar--striped" : "",
                    ].join(" ")}
                    style={{ left: `${left}%`, width: `${width}%` }}
                    title={`${task.taskTitle} (${fmt(s)} → ${fmt(e)})`}
                    onMouseEnter={(e) =>
                      setTip({ x: e.clientX, y: e.clientY, lines: tipLines })
                    }
                    onMouseLeave={() => setTip(null)}
                  >
                    {/* progress fill */}
                    {typeof progress === "number" && (
                      <span
                        className="gantt__barProgress"
                        style={{ width: `${progress}%` }}
                        aria-hidden
                      />
                    )}

                    {/* diagonal hatch layer always visible for “////////” look */}
                    <span className="gantt__barHatch" aria-hidden />

                    {/* start/end badges when wide enough */}
                    {width > 12 && (
                      <>
                        <span className="gantt__dateBadge gantt__dateBadge--start">
                          {fmt(s)}
                        </span>
                        <span className="gantt__dateBadge gantt__dateBadge--end">
                          {fmt(e)}
                        </span>
                      </>
                    )}
                  </button>
                ) : (
                  <div
                    className="gantt__bar gantt__bar--placeholder"
                    title="Missing dates"
                  />
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* legend */}
      <div className="gantt__legend">
        <LegendSwatch cls="bar--green" label="Low / On Track" />
        <LegendSwatch cls="bar--blue" label="Medium / In Progress" />
        <LegendSwatch cls="bar--amber" label="High / At Risk" />
        <LegendSwatch cls="bar--red" label="Critical / Blocked" />
      </div>
      <div className="heading">
        <h4>This is a Beta Version Does Not Represent Final Quality</h4>
      </div>

      {/* tooltip */}
      {tip && (
        <div
          className="gantt__tip"
          style={{ left: tip.x + 10, top: tip.y + 10 }}
        >
          {tip.lines.map((l, i) => (
            <div key={i}>{l}</div>
          ))}
        </div>
      )}
    </div>
  );
}

function LegendSwatch({ cls, label }) {
  return (
    <>
      <div className="gantt__swatch">
        <span className={["gantt__swatchDot", cls].join(" ")} />
        <span>{label}</span>
      </div>
    </>
  );
}
